import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import cookie from "react-cookies";
import constants from "../Constants";
import {
  QIKTRUCK_PRIVACY_URL,
  QIKTRUCK_TERMS_URL,
} from "../constants/legal";
import { saveMediaAccessCookie } from "../utils/mediaAccessCookie";
import "./CustomerOtpForm.css";

const baseURL = constants.baseURL;
const OTP_LENGTH = 6;
const RESEND_SECONDS = 45;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COOKIE_OPTS = { path: "/", maxAge: 86400, secure: true };

function safeRedirectPath() {
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get("redirect") || params.get("returnTo");
  if (redirect && redirect.startsWith("/") && !redirect.startsWith("//")) {
    return redirect;
  }
  return "/dashboard";
}

function normalizeOtp(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, OTP_LENGTH)
    .toUpperCase();
}

function sendErrorMessage(error) {
  const status = error && error.response && error.response.status;
  if (status === 403) {
    return "Too many codes sent to this email. Please wait and try again.";
  }
  return "We could not send a code. Check the email or contact support.";
}

export default function CustomerOtpForm({
  mode = "signup",
  variant = "page",
  onStepChange,
}) {
  const isLogin = mode === "login";
  const isCompact = variant === "compact";

  const [loading, setLoading] = useState(false);
  const [apierror, setApierror] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpId, setOtpId] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const emailRef = useRef(null);
  const otpRef = useRef(null);
  const lastAutoSubmitted = useRef("");
  const submitLock = useRef(false);

  const emailValid = email !== "" && EMAIL_REGEX.test(email);
  const otpValid = otp.length === OTP_LENGTH;

  useEffect(() => {
    if (!isCompact && !showOtp && emailRef.current) {
      emailRef.current.focus();
    }
  }, [isCompact, showOtp]);

  useEffect(() => {
    if (showOtp && otpRef.current) {
      otpRef.current.focus();
    }
  }, [showOtp]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setInterval(() => {
      setResendIn((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const notifyStep = (step) => {
    if (typeof onStepChange === "function") {
      onStepChange(step);
    }
  };

  const requestCode = (isResend) => {
    if (!emailValid || loading || submitLock.current) return;
    submitLock.current = true;
    setLoading(true);
    setApierror("");
    setMessage("");
    axios
      .post(baseURL + "/customer-otp", { email: email.trim() })
      .then((response) => {
        setOtpId(response.data && response.data.id);
        setShowOtp(true);
        setOtp("");
        lastAutoSubmitted.current = "";
        setResendIn(RESEND_SECONDS);
        notifyStep("otp");
        if (isResend) {
          setMessage("A new code is on the way. Check your inbox.");
        }
      })
      .catch((error) => {
        setApierror(sendErrorMessage(error));
      })
      .finally(() => {
        submitLock.current = false;
        setLoading(false);
      });
  };

  const saveSessionAndGo = (data) => {
    cookie.save("session-id-qt", data["session-id-qt"], COOKIE_OPTS);
    saveMediaAccessCookie(data["session-id-qt"]);
    cookie.save("ctName", data.firstname, COOKIE_OPTS);
    cookie.save("localsessionid", data.customerid, COOKIE_OPTS);
    cookie.save("promocode", data.promocode, COOKIE_OPTS);
    if (!isLogin && typeof window.fbq === "function") {
      window.fbq("track", "CompleteRegistration", {
        content_name: "QikTruck Business account",
        status: true,
      });
    }
    setMessage(isLogin ? "Signed in. Redirecting..." : "Account ready. Redirecting...");
    window.location.href = safeRedirectPath();
  };

  const verifyCode = (code) => {
    const nextOtp = normalizeOtp(code);
    if (nextOtp.length !== OTP_LENGTH || loading || submitLock.current) return;
    submitLock.current = true;
    setLoading(true);
    setApierror("");
    setMessage("");
    axios
      .post(baseURL + "/validate-customer-otp", {
        email: email.trim(),
        otp: nextOtp,
        id: otpId,
      })
      .then((response) => {
        if (response.data && response.data.message && response.data.message.length > 0) {
          setMessage(response.data.message);
          return;
        }
        saveSessionAndGo(response.data);
      })
      .catch(() => {
        setApierror("That code is incorrect or expired. Try again.");
      })
      .finally(() => {
        submitLock.current = false;
        setLoading(false);
      });
  };

  const handleEmailSubmit = (event) => {
    event.preventDefault();
    requestCode(false);
  };

  const handleOtpSubmit = (event) => {
    event.preventDefault();
    verifyCode(otp);
  };

  const handleOtpChange = (event) => {
    const next = normalizeOtp(event.target.value);
    setOtp(next);
    setApierror("");
    if (next.length === OTP_LENGTH && lastAutoSubmitted.current !== next) {
      lastAutoSubmitted.current = next;
      verifyCode(next);
    }
  };

  const handleBack = () => {
    setShowOtp(false);
    setOtp("");
    setApierror("");
    setMessage("");
    lastAutoSubmitted.current = "";
    notifyStep("email");
  };

  const emailButtonLabel = loading
    ? "Sending code..."
    : isLogin
      ? "Send code"
      : "Continue";

  const verifyButtonLabel = loading ? "Verifying..." : "Verify code";

  return (
    <div className={`qt-otp ${isCompact ? "qt-otp--compact" : "qt-otp--page"}`}>
      {showOtp ? (
        <form onSubmit={handleOtpSubmit} noValidate>
          <button type="button" className="qt-otp__back" onClick={handleBack}>
            <i className="mdi mdi-arrow-left" aria-hidden="true" />
            Back
          </button>
          {isCompact ? (
            <p className="qt-otp__compact-lede">
              Sent to <span className="qt-otp__email-strong">{email}</span>. Check spam if it is not there.
            </p>
          ) : (
            <>
              <h1 className="qt-otp__title">Enter verification code</h1>
              <p className="qt-otp__lede">
                We sent a code to <span className="qt-otp__email-strong">{email}</span>.
                Check spam if it is not there. Codes are valid for 24 hours.
              </p>
            </>
          )}
          <div className="qt-otp__card">
            {apierror ? (
              <p className="qt-otp__banner" role="alert" aria-live="assertive">
                {apierror}
              </p>
            ) : null}
            {message ? (
              <p className="qt-otp__banner qt-otp__banner--ok" role="status" aria-live="polite">
                {message}
              </p>
            ) : null}
            <div className="qt-otp__field">
              <label className="qt-otp__label" htmlFor="qt-otp-code">
                Verification code
              </label>
              <input
                ref={otpRef}
                id="qt-otp-code"
                name="one-time-code"
                className="qt-otp__input qt-otp__input--otp"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={OTP_LENGTH}
                placeholder="XXXXXX"
                value={otp}
                disabled={loading}
                aria-invalid={otp !== "" && !otpValid}
                aria-describedby="qt-otp-hint"
                onChange={handleOtpChange}
              />
              <p id="qt-otp-hint" className="qt-otp__hint">
                6 letters or numbers. We match the code in uppercase.
              </p>
              {otp !== "" && !otpValid ? (
                <p className="qt-otp__error">Enter all 6 characters from the email.</p>
              ) : null}
            </div>
            <button
              type="submit"
              className="qt-otp__submit"
              disabled={!otpValid || loading}
            >
              {loading ? (
                <i className="mdi mdi-loading mdi-spin qt-otp__submit-icon" aria-hidden="true" />
              ) : (
                <i className="mdi mdi-check-circle-outline qt-otp__submit-icon" aria-hidden="true" />
              )}
              {verifyButtonLabel}
            </button>
            <p className="qt-otp__resend">
              Did not get it?{" "}
              {resendIn > 0 ? (
                <span>Resend available in {resendIn}s</span>
              ) : (
                <button
                  type="button"
                  className="qt-otp__resend-btn"
                  onClick={() => requestCode(true)}
                  disabled={loading}
                >
                  Resend code
                </button>
              )}
            </p>
          </div>
        </form>
      ) : (
        <form onSubmit={handleEmailSubmit} noValidate>
          {isCompact ? null : (
            <>
              <h1 className="qt-otp__title">
                {isLogin ? "Sign in" : "Create your account"}
              </h1>
              <p className="qt-otp__lede">
                Enter your work email. We will send a 6-character code. No password needed.
              </p>
            </>
          )}
          <div className="qt-otp__card">
            {apierror ? (
              <p className="qt-otp__banner" role="alert" aria-live="assertive">
                {apierror}
              </p>
            ) : null}
            {isCompact ? (
              <div className="qt-signup-row">
                <input
                  ref={emailRef}
                  id="qt-otp-email"
                  name="email"
                  className="qt-otp__input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@company.com"
                  aria-label="Email address"
                  value={email}
                  disabled={loading}
                  aria-invalid={email !== "" && !emailValid}
                  onChange={(event) => {
                    setEmail(event.target.value.trimStart());
                    setApierror("");
                  }}
                />
                <button
                  type="submit"
                  className="qt-otp__submit"
                  disabled={!emailValid || loading}
                >
                  {emailButtonLabel}
                </button>
              </div>
            ) : (
              <div className="qt-otp__field">
                <label className="qt-otp__label" htmlFor="qt-otp-email">
                  Email address
                </label>
                <input
                  ref={emailRef}
                  id="qt-otp-email"
                  name="email"
                  className="qt-otp__input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@company.com"
                  value={email}
                  disabled={loading}
                  aria-invalid={email !== "" && !emailValid}
                  onChange={(event) => {
                    setEmail(event.target.value.trimStart());
                    setApierror("");
                  }}
                />
              </div>
            )}
            {email !== "" && !emailValid ? (
              <p className="qt-otp__error">Please enter a valid email address.</p>
            ) : null}
            {isCompact ? null : (
              <button
                type="submit"
                className="qt-otp__submit"
                disabled={!emailValid || loading}
              >
                {loading ? (
                  <i className="mdi mdi-loading mdi-spin qt-otp__submit-icon" aria-hidden="true" />
                ) : null}
                {emailButtonLabel}
              </button>
            )}
          </div>
          <p className="qt-otp__legal">
            {isLogin ? (
              <>
                New to QikTruck Business?{" "}
                <a href="/signup">Create an account</a>.
              </>
            ) : (
              <>
                By continuing you agree to the{" "}
                <a href={QIKTRUCK_TERMS_URL} target="_blank" rel="noopener noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href={QIKTRUCK_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                . Already have an account? <a href="/login">Sign in</a>.
              </>
            )}
          </p>
        </form>
      )}
    </div>
  );
}
